
import { validationMixin } from "vuelidate";
import { SistemaMixin, FormatMixin } from "@/modules/Application";
import { mapGetters, mapActions } from "vuex";
import AssinaturaData, { AssinaturaDataRequired } from "./AssinaturaData";
import CardForm from "../Cards/CardForm";

export default {
  name: "app-assinatura-renovacao-2026",
  mixins: [validationMixin, SistemaMixin, FormatMixin],
  components: { CardForm },

  data() {
    return {
      dataReady: false,
      ocultaPlans: true,
      step: 1,

      assinatura: {},
      assinaturaResumoPayload: {},

      plugins: {
        certificado: {
          nome: "Certificado Digital A1",
          valor: 190,
          ativo: false,
        },
        tef: {
          nome: "TEF Integrado",
          valor: 1069.90,
          ativo: false,
        },
      },

      // TABELA
      assinaturas: [],
      assinaturasLoading: false,

      // Loading
      isLoading: false,
      pixLoading: false,
      pixStatusLoading: false,
      renovacaoConcluida: false,
      telaEncerrada: false,

      // Pagamento
      opcaoSelecionada: null,

      // CARTÃO (CardForm)
      chamaCartao: false,
      vezesByPlan: 1,
      formData: {
        cardName: "",
        cardNumber: "",
        cardMonth: "",
        cardYear: "",
        cardCvv: "",
      },

      // PIX
      pixPaymentId: null,
      qrCodePix: null,
      codigoPix: null,
      pixPollTimer: null,
      pixExpirationDate: null,

      pixGerado: false,
      pixLastAmount: null,
      pixConfirmado: false,
      bloquearSaidaPix: false,
      appsPendingRenewalSelecionadosCodes: [],
      // Regras 2026
      planos2026: {}
    }
  },

  validations: { form: AssinaturaDataRequired },

  computed: {
    ...mapGetters([
      "itsblocked",
      "idRevenda",
      "dadosRevenda",
      "licenca",
      "isRevenda",
      "isNuts",
      "isClienteRevenda",
      "isClienteNutsDireto",
    ]),

    contaAssinaturaFlags() {
      const resumo = this.assinaturaResumoPayload || {};

      const isRevendaPayload = this.readBoolean(
        resumo.is_revenda,
        resumo.isRevenda,
        resumo.revenda,
      );

      const isClienteRevendaPayload = this.readBoolean(
        resumo.is_cliente_revenda,
        resumo.isClienteRevenda,
        resumo.cliente_revenda,
      );

      const isClienteNutsDiretoPayload = this.readBoolean(
        resumo.is_cliente_nuts_direto,
        resumo.isClienteNutsDireto,
        resumo.cliente_nuts_direto,
      );

      return {
        isRevenda: isRevendaPayload ?? !!this.isRevenda,
        isClienteRevenda: isClienteRevendaPayload ?? !!this.isClienteRevenda,
        isClienteNutsDireto: isClienteNutsDiretoPayload ?? !!this.isClienteNutsDireto,
      };
    },

    shouldShowAppsContratadosSection() {
      const flags = this.contaAssinaturaFlags;
      if (flags.isRevenda || flags.isClienteRevenda) return false;
      return flags.isClienteNutsDireto === true;
    },

    planoPrincipalNome() {
      const plano = this.assinatura?.plano;
      if (typeof plano === "string") return plano;
      return plano?.nome || plano?.name || "Plano atual";
    },

    planoPrincipalStatusLabel() {
      if (typeof this.assinatura?.status === "boolean") {
        return this.assinatura.status ? "Ativo" : "Inativo";
      }

      if (this.assinatura?.status === 1 || this.assinatura?.status === "1") return "Ativo";
      if (this.assinatura?.status === 0 || this.assinatura?.status === "0") return "Inativo";

      const status = String(this.assinatura?.status || "").toLowerCase();
      const statusMap = {
        active: "Ativo",
        ativo: "Ativo",
        paid: "Ativo",
        trialing: "Em teste",
        pending: "Pendente",
        overdue: "Em atraso",
        canceled: "Cancelado",
        cancelado: "Cancelado",
        inactive: "Inativo",
        inativo: "Inativo",
      };

      return statusMap[status] || "Ativo";
    },

    isPlanoPrincipalAnual() {
      return true;
    },

    appsContratadosRaw() {
      const resumo = this.assinaturaResumoPayload || {};
      return (
        resumo.apps_contratados ||
        resumo.appsContratados ||
        resumo.empresa_features ||
        resumo.empresaFeatures ||
        resumo.features_contratadas ||
        resumo.featuresContratadas ||
        []
      );
    },

    appsContratadosNormalizados() {
      if (!Array.isArray(this.appsContratadosRaw)) return [];

      return this.appsContratadosRaw
        .filter((item) => this.isAppAtivo(item))
        .map((item, index) => {
          const quantity = Math.max(1, Number(item.quantity ?? item.quantidade ?? 1));
          const unitPrice = this.readCurrency(
            item.unit_price,
            item.unitPrice,
            item.valor_unitario,
            item.preco_unitario,
            item.price,
          );

          const informedTotal = this.readCurrency(
            item.total_price,
            item.totalPrice,
            item.valor_total,
            item.preco_total,
            item.valor,
          );

          const totalPrice = informedTotal || (quantity > 1 ? unitPrice * quantity : unitPrice);

          return {
            id: item.id || item.feature_id || item.slug || `${item.nome || "app"}-${index}`,
            nome: item.nome || item.name || item.titulo || "App",
            tipoLabel: this.translateAppType(item),
            quantity,
            quantityLabel: item.quantity_label || item.quantityLabel || item.label_quantidade || "unidades",
            unitPrice,
            totalPrice,
            source: item.source,
            sourceLabel: this.translateAppSource(item.source),
            hasSource: !!item.source,
            shouldShowIncluso: String(item.source || "").toLowerCase() === "legacy" && Number(totalPrice) === 0,
            showSourceLine:
              !!item.source &&
              !(String(item.source || "").toLowerCase() === "legacy" && Number(totalPrice) === 0),
          };
        });
    },

    appsPendingRenewalRaw() {
      const resumo = this.assinaturaResumoPayload || {};
      return (
        resumo.apps_ativos_renovacao ||
        resumo.appsAtivosRenovacao ||
        resumo.apps_mensais_ativos ||
        resumo.appsMensaisAtivos ||
        resumo.apps_contratados_mensais ||
        resumo.appsContratadosMensais ||
        resumo.apps_monthly_to_yearly ||
        resumo.appsMonthlyToYearly ||
        resumo.monthly_apps_active ||
        resumo.activeMonthlyApps ||
        resumo.apps_pending_renewal_charge ||
        resumo.appsPendingRenewalCharge ||
        resumo.apps_pendentes_cobranca_renovacao ||
        resumo.appsPendentesCobrancaRenovacao ||
        resumo.apps_cortesia_renovacao ||
        resumo.appsCortesiaRenovacao ||
        []
      );
    },

    appsPendingRenewalNormalizados() {
      if (!Array.isArray(this.appsPendingRenewalRaw)) return [];

      return this.appsPendingRenewalRaw
        .filter((item) => this.isAppAtivo(item))
        .map((item, index) => {
          const code =
            item.code ||
            item.feature_code ||
            item.featureCode ||
            item.codigo ||
            item.slug ||
            item.feature?.code ||
            "";
          const quantity = Math.max(1, Number(item.quantity ?? item.quantidade ?? 1));
          const informedUnitPrice = this.readCurrency(
            item.unit_price,
            item.unitPrice,
            item.valor_unitario,
            item.preco_unitario,
            item.monthly_price,
            item.monthlyPrice,
            item.valor_mensal,
            item.preco_mensal,
            item.price,
          );

          const informedMonthlyTotal = this.readCurrency(
            item.total_price,
            item.totalPrice,
            item.valor_total,
            item.preco_total,
            item.monthly_total,
            item.monthlyTotal,
            item.total_monthly,
            item.valor_mensal_total,
            item.preco_mensal_total,
            item.valor,
          );

          const unitPrice =
            informedUnitPrice ||
            (informedMonthlyTotal ? informedMonthlyTotal / quantity : 0);
          const totalPrice = informedMonthlyTotal || unitPrice * quantity;
          const informedAnnualPrice = this.readCurrency(
            item.annual_price,
            item.annualPrice,
            item.valor_anual,
            item.preco_anual,
            item.annual_total,
            item.annualTotal,
            item.total_anual,
          );
          const nome = item.nome || item.name || item.titulo || "App";
          const rawQuantityLabel = item.quantity_label || item.quantityLabel || item.label_quantidade || "unidades";

          return {
            id: item.id || item.feature_id || item.slug || code || `app-pending-${index}`,
            code,
            nome,
            quantity,
            quantityLabel: this.pluralizeQuantityLabel(rawQuantityLabel, quantity),
            unitPrice,
            totalPrice,
            annualPrice: informedAnnualPrice || totalPrice * 12,
          };
        })
        .filter((app) => !!app.code);
    },

    appsPendingRenewalSelecionados() {
      return this.appsPendingRenewalNormalizados.filter((app) =>
        this.appsPendingRenewalSelecionadosCodes.includes(app.code),
      );
    },

    appsPendingRenewalTotalSelecionado() {
      return this.appsPendingRenewalSelecionados.reduce(
        (acc, app) => acc + this.readCurrency(app.annualPrice),
        0,
      );
    },

    planoPrincipalAnual() {
      return this.readCurrency(this.assinatura?.valor);
    },

    appsContratadosTotalMensal() {
      return this.appsContratadosNormalizados.reduce(
        (acc, app) => acc + this.readCurrency(app.totalPrice),
        0,
      );
    },

    valorPlanoResumoRenovacao() {
      return this.valorPlano || this.planoPrincipalAnual;
    },

    totalRenovacaoPlanoApps() {
      return Number(this.valorPlanoResumoRenovacao) + Number(this.appsPendingRenewalTotalSelecionado);
    },

    totalRenovacaoResumo() {
      return Number(this.totalRenovacaoPlanoApps) + Number(this.valorPlugins);
    },

    totalMensalAssinatura() {
      return Number(this.planoPrincipalAnual) + Number(this.appsContratadosTotalMensal);
    },

    showAssinaturaCard() {
      if (this.idRevenda != 1) return false;

      const planosValidos = ["1", "2", "3", "4", "6", "7"];
      if (!planosValidos.includes(String(this.assinatura.id_plan))) return false;

      if (!this.assinatura.data_validade) return true;

      const hoje = new Date();
      hoje.setHours(0, 0, 0, 0);

      const validade = new Date(this.assinatura.data_validade);
      validade.setHours(0, 0, 0, 0);

      // Permite renovação a partir de 30 dias antes do vencimento
      const diasAntesVencimento = 30;
      const dataLimite = new Date(validade);
      dataLimite.setDate(dataLimite.getDate() - diasAntesVencimento);

      return hoje >= dataLimite;
    },

    planoAtual() {
      return this.planos2026[String(this.assinatura.id_plan)] || null;
    },

    opcoesOrdenadas() {
      if (!this.planoAtual) return {};
      const { prioridade, opcoes } = this.planoAtual;

      const ordered = {};
      (prioridade || []).forEach((k) => {
        if (opcoes[k]) ordered[k] = opcoes[k];
      });

      Object.keys(opcoes || {}).forEach((k) => {
        if (!ordered[k]) ordered[k] = opcoes[k];
      });

      return ordered;
    },

    valorPlano() {
      if (!this.planoAtual || !this.opcaoSelecionada) return 0;
      const opcao = this.planoAtual.opcoes?.[this.opcaoSelecionada] || {};
      const valorTotal = this.readCurrency(opcao.valor_total, opcao.valorTotal);
      if (valorTotal) return valorTotal;

      const parcela = Number(opcao.parcela || 0);
      const valorBase = this.readCurrency(opcao.valor);
      return parcela > 1 ? valorBase * parcela : valorBase;
    },

    valorPlugins() {
      return Object.values(this.plugins)
        .filter((p) => p.ativo)
        .reduce((acc, p) => acc + this.readCurrency(p.valor), 0);
    },

    valorSelecionado() {
      return Number(this.valorPlano) + Number(this.appsPendingRenewalTotalSelecionado) + Number(this.valorPlugins);
    },

    modalidadeSelecionada() {
      if (!this.planoAtual || !this.opcaoSelecionada) return null;
      return this.planoAtual.opcoes?.[this.opcaoSelecionada]?.modalidade || null;
    },

    // tabela
    itemsTable() {
      return (this.assinaturas || []).map((b) => ({
        data_vencimento: b.data_vencimento,
        valor: b.valor,
        pagamento: !!b.recebido,
        cancelado: this.readBoolean(b.cancelado, b.canceled, b.cancelled) === true,
        status_raw: b.status || b.situacao || null,
        url_boleto: b.url_boleto,
      }));
    },

    fieldsAssinaturas() {
      return [
        { key: "data_vencimento", label: "Vencimento" },
        { key: "valor", label: "Valor" },
        { key: "status", label: "Status" },
        { key: "acoes", label: "Ações" },
      ];
    },
  },

  watch: {
    // Se mudou valor final (plugins), invalida PIX gerado para evitar QR errado
    valorSelecionado() {
      if (this.opcaoSelecionada === "pix" && this.pixGerado) {
        this.resetPixState();
      }
    },
  },

  beforeDestroy() {
    this.telaEncerrada = true;
    this.stopPixPolling();
    window.removeEventListener("beforeunload", this.handleBeforeUnload);
  },

  async mounted() {
    window.addEventListener("beforeunload", this.handleBeforeUnload);
    await this.getDataInitSafe();
  },

  methods: {
    ...mapActions(["setLoading"]),

    readBoolean(...values) {
      for (const value of values) {
        if (typeof value === "boolean") return value;
        if (value === 1 || value === "1") return true;
        if (value === 0 || value === "0") return false;

        if (typeof value === "string") {
          const normalized = value.trim().toLowerCase();
          if (["true", "sim", "yes"].includes(normalized)) return true;
          if (["false", "nao", "não", "no"].includes(normalized)) return false;
        }
      }

      return null;
    },

    readCurrency(...values) {
      for (const value of values) {
        if (value === null || value === undefined || value === "") continue;

        if (typeof value === "number" && !Number.isNaN(value)) {
          return value;
        }

        const rawValue = String(value).trim().replace(/[^\d,.-]/g, "");
        if (!rawValue) continue;

        let normalizedValue = rawValue;

        if (rawValue.includes(".") && rawValue.includes(",")) {
          normalizedValue = rawValue.replace(/\./g, "").replace(",", ".");
        } else if (rawValue.includes(",")) {
          normalizedValue = rawValue.replace(",", ".");
        }

        const asNumber = Number(normalizedValue);
        if (!Number.isNaN(asNumber)) return asNumber;
      }

      return 0;
    },

    isAppAtivo(item = {}) {
      const explicitActive = this.readBoolean(
        item.ativo,
        item.active,
        item.is_active,
        item.isActive,
      );

      if (explicitActive !== null) return explicitActive;

      const status = String(item.status || "").trim().toLowerCase();
      if (["inativo", "inactive", "cancelado", "canceled", "disabled"].includes(status)) {
        return false;
      }

      return true;
    },

    translateAppSource(source) {
      const sourceMap = {
        marketplace: "Contratado",
        legacy: "Incluso",
        manual: "Liberado pela Nuts",
        revenda: "Liberado pela revenda",
      };

      return sourceMap[String(source || "").toLowerCase()] || "Contratado";
    },

    translateAppType(item = {}) {
      const rawType = String(item.tipo || item.type || item.kind || "").toLowerCase();

      if (["plugin", "plugins"].includes(rawType)) return "Plugin";
      if (["modulo", "módulo", "module", "modules"].includes(rawType)) return "Módulo";

      const pluginFlag = this.readBoolean(item.is_plugin, item.isPlugin, item.plugin);
      if (pluginFlag === true) return "Plugin";

      return "Módulo";
    },

    pluralizeQuantityLabel(label, quantity) {
      const normalizedLabel = String(label || "unidades").trim();
      if (Number(quantity) <= 1) return normalizedLabel;
      if (normalizedLabel.endsWith("s")) return normalizedLabel;
      return `${normalizedLabel}s`;
    },

    appPendingRenewalPriceLabel(app) {
      if (app.quantity > 1) {
        return `${app.quantity} ${app.quantityLabel} x R$ ${this.formatValorReal(app.unitPrice)}/mês → R$ ${this.formatValorReal(app.annualPrice)}/ano`;
      }

      return `R$ ${this.formatValorReal(app.totalPrice)}/mês → R$ ${this.formatValorReal(app.annualPrice)}/ano`;
    },

    valorOpcaoTotalComApps(opcao = {}) {
      const parcela = Number(opcao.parcela || 0);
      const valorTotalOpcao = this.readCurrency(opcao.valor_total, opcao.valorTotal);

      if (valorTotalOpcao) {
        return valorTotalOpcao + Number(this.appsPendingRenewalTotalSelecionado);
      }

      const valorBase = this.readCurrency(opcao.valor);
      const valorTotal = parcela > 1 ? valorBase * parcela : valorBase;
      return valorTotal + Number(this.appsPendingRenewalTotalSelecionado);
    },

    valorOpcaoParcelaComApps(opcao = {}) {
      const parcela = Number(opcao.parcela || 1);
      return this.valorOpcaoTotalComApps(opcao) / Math.max(1, parcela);
    },

    valorOpcaoMensalComApps(opcao = {}) {
      return this.readCurrency(opcao.valor) + (Number(this.appsPendingRenewalTotalSelecionado) / 12);
    },

    selecionarTodosAppsPendingRenewal() {
      this.appsPendingRenewalSelecionadosCodes = this.appsPendingRenewalNormalizados.map(
        (app) => app.code,
      );
    },

    isAppPendingRenewalSelected(code) {
      return this.appsPendingRenewalSelecionadosCodes.includes(code);
    },

    toggleAppPendingRenewal(code, checked) {
      if (this.isLoading || this.pixLoading || this.pixConfirmado || this.renovacaoConcluida) return;
      if (!code) return;

      const selected = checked === true || checked === "true" || checked === 1;
      const currentCodes = this.appsPendingRenewalSelecionadosCodes.filter(
        (itemCode) => itemCode !== code,
      );

      this.appsPendingRenewalSelecionadosCodes = selected
        ? [...currentCodes, code]
        : currentCodes;
    },

    appsRenovacaoPayload() {
      return this.appsPendingRenewalSelecionados.map((app) => ({
        code: app.code,
        quantity: app.quantity,
        origin: "monthly_to_yearly",
      }));
    },

    // ===== CardForm handlers =====
    updateCardNumber(v) {
      this.formData.cardNumber = v;
    },
    updateCardName(v) {
      this.formData.cardName = v;
    },
    updateCardMonth(v) {
      this.formData.cardMonth = v;
    },
    updateCardYear(v) {
      this.formData.cardYear = v;
    },
    updateCardCvv(v) {
      this.formData.cardCvv = v;
    },

    togglePlugin(key) {
      if (this.isLoading || this.pixLoading || this.pixConfirmado || this.renovacaoConcluida) return;
      if (!this.plugins?.[key]) return;
      this.plugins[key].ativo = !this.plugins[key].ativo;
    },

    handleBeforeUnload(event) {
      if (!this.bloquearSaidaPix) return;

      event.preventDefault();
      event.returnValue = "";
    },

    // ===== INIT =====
    async getDataInitSafe() {
      this.setLoading(true);
      try {
        this.dataReady = true;
        const dataPlanos = await this.$http.post("/empresa/planos-ativos");

        if (dataPlanos.data) {
          this.planos2026 = dataPlanos.data.planos2026;
        }

        const data = await this.getAssinaturaByClient();
        this.assinaturaResumoPayload = data || {};
        this.assinatura = new AssinaturaData(data);
        this.selecionarTodosAppsPendingRenewal();

        if (this.plugins?.certificado) {
          this.plugins.certificado.ativo = !!data.plugin_certificado;
        }

        if (this.plugins?.tef) {
          this.plugins.tef.ativo = !!data.plugin_tef;
        }

        this.ocultaPlans = !(Number(this.assinatura.id_plan) === 1);

        if (this.assinatura.id_empresa === 1) {
          this.ocultaPlans = false;
        }

        if (this.isRevenda) {
          this.ocultaPlans = false;
        }

        await this.carregarAssinaturas();
      } finally {
        this.setLoading(false);
      }
    },

    async handleCardSuccess() {
      await this.finalizarRenovacao();
    },

    async carregarAssinaturas() {
      this.assinaturasLoading = true;
      try {
        const res = await this.$http.post("/empresa/fatura-assinaturas", {
          id_empresa: this.assinatura.id_empresa
        });
        this.assinaturas = res?.data?.assinaturas || [];
      } catch (e) {
        this.assinaturas = [];
      } finally {
        this.assinaturasLoading = false;
      }
    },

    // ===== Format =====
    formatValorReal(value) {
      const num = Number(String(value ?? 0).replace(",", "."));
      return (isNaN(num) ? 0 : num).toLocaleString("pt-BR", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },

    formatDate(dt) {
      if (!dt) return "";
      if (/^\d{4}-\d{2}-\d{2}$/.test(dt)) {
        const [y, m, d] = dt.split("-").map(Number);
        const date = new Date(y, m - 1, d);
        return date.toLocaleDateString("pt-BR");
      }
      const date = new Date(dt);
      return isNaN(date) ? "" : date.toLocaleDateString("pt-BR");
    },

    formatDateTime(dt) {
      if (!dt) return "";
      const date = new Date(String(dt).replace(" ", "T"));
      return isNaN(date) ? dt : date.toLocaleString("pt-BR");
    },

    // ===== Seleção de pagamento (SEM back aqui) =====
    async selecionarOpcao(key) {
      if (this.isLoading || this.pixLoading || this.pixConfirmado || this.renovacaoConcluida) return;
      this.opcaoSelecionada = key;

      // reset UI final
      this.chamaCartao = false;

      // trocar forma de pagamento invalida pix anterior
      this.resetPixState();

      // só define parcelas (CardForm abre apenas no passo 3, no clicar Renovar agora)
      if (String(key).startsWith("cartao")) {
        const parcela = this.planoAtual?.opcoes?.[key]?.parcela;
        this.vezesByPlan = Number(parcela || 1);
      }
    },

    // ===== CTA FINAL =====
    async iniciarPagamento() {
      if (this.isLoading || this.pixLoading || this.renovacaoConcluida) return;
      if (!this.opcaoSelecionada) {
        this.$toast?.danger?.("Selecione uma forma de pagamento.");
        return;
      }

      if (this.step !== 3) {
        this.step = 3;
        return;
      }

      // PIX: só gera aqui (passo 3)
      if (this.opcaoSelecionada === "pix") {
        const precisaRegerar =
          this.pixLastAmount !== Number(this.valorSelecionado) || !this.pixGerado;

        await this.gerarPixQRCode(precisaRegerar);
        return;
      }

      // Cartão: abre CardForm aqui
      if (String(this.opcaoSelecionada).startsWith("cartao")) {
        this.chamaCartao = true;

        this.$nextTick(() => {
          const el = this.$el.querySelector(".card-resumo");
          el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
        });

        return;
      }

      // Boleto / outros
      await this.finalizarRenovacao();
    },

    // ===== PIX helpers =====
    resetPixState() {
      this.stopPixPolling?.();
      this.pixPaymentId = null;
      this.qrCodePix = null;
      this.codigoPix = null;
      this.pixExpirationDate = null;
      this.pixGerado = false;
      this.pixLastAmount = null;
      this.pixConfirmado = false;
      this.bloquearSaidaPix = false;
    },

    async gerarPixQRCode(force = false) {
      if (this.step !== 3 || this.pixLoading || this.isLoading || this.renovacaoConcluida) return;

      if (!this.valorSelecionado || Number(this.valorSelecionado) <= 0) {
        this.$toast?.danger?.("Não foi possível calcular o valor do PIX.");
        return;
      }

      const pixExpirado = this.pixExpirationDate &&
        new Date(String(this.pixExpirationDate).replace(" ", "T")).getTime() <= Date.now();
      if (!force && !pixExpirado && this.pixPaymentId && this.qrCodePix && this.codigoPix) {
        this.startPixPolling();
        this.pixGerado = true;
        this.bloquearSaidaPix = true;
        return;
      }

      this.stopPixPolling?.();
      this.pixLoading = true;

      this.pixGerado = false;
      this.pixConfirmado = false;
      this.bloquearSaidaPix = true;
      this.qrCodePix = null;
      this.codigoPix = null;
      this.pixPaymentId = null;
      this.pixExpirationDate = null;

      try {
        this.setLoading(true);

        const res = await this.$http.post("/boletos/pix", {
          id_empresa: this.assinatura.id_empresa,
          id_assinatura: this.assinatura.id_assinatura,
          id_plan: this.assinatura.id_plan,
          modalidade: this.modalidadeSelecionada,
          forma_pagamento: "pix",
          valor: this.valorSelecionado,
          email_fatura: this.licenca.email,
          cnpj_cpf: this.assinatura.cnpj_cpf,
          apps_renovacao: this.appsRenovacaoPayload(),
          plugins: Object.keys(this.plugins)
            .filter((k) => this.plugins[k]?.ativo)
            .map((k) => ({
              key: k,
              nome: this.plugins[k].nome,
              valor: this.plugins[k].valor,
            })),
        });

        if (this.telaEncerrada) return;
        const data = res?.data || {};

        this.pixPaymentId = data.id || null;

        if (data.encodedImage) {
          this.qrCodePix = `data:image/png;base64,${data.encodedImage}`;
        } else {
          this.qrCodePix = data.qrcode_base64 || null;
        }

        this.codigoPix = data.payload || data.copia_cola || null;
        this.pixExpirationDate = data.expirationDate || null;

        if (this.pixPaymentId) {
          this.pixGerado = true;
          this.pixLastAmount = Number(this.valorSelecionado);
          this.startPixPolling();
        } else {
          this.bloquearSaidaPix = false;
          this.$toast?.danger?.("Não foi possível gerar o PIX (ID ausente).");
        }
      } catch (e) {
        this.bloquearSaidaPix = false;
        this.$toast?.danger?.(e?.response?.data?.msg || "Erro ao gerar PIX. Tente novamente.");
      } finally {
        this.pixLoading = false;
        this.setLoading(false);
      }
    },

    startPixPolling() {
      this.stopPixPolling();
      if (!this.telaEncerrada && !this.renovacaoConcluida) {
        this.pixPollTimer = setInterval(() => this.checkPixStatus(), 8000);
      }
    },

    stopPixPolling() {
      if (this.pixPollTimer) clearInterval(this.pixPollTimer);
      this.pixPollTimer = null;
    },

    async checkPixStatus() {
      if (!this.pixPaymentId || this.pixStatusLoading || this.renovacaoConcluida || this.telaEncerrada) return;
      const pixPaymentId = this.pixPaymentId;
      this.pixStatusLoading = true;

      try {
        const res = await this.$http.post('/boletos/status-pix/', {
          id_pix: pixPaymentId
        });
        if (this.telaEncerrada || this.pixPaymentId !== pixPaymentId) return;

        const pagamentos = res?.data?.data || [];

        const pagamentoPix = pagamentos.find(
          item => item.pixQrCodeId === this.pixPaymentId
        );

        const status = pagamentoPix?.status;

        if (status === "RECEIVED") {
          this.pixConfirmado = true;
          await this.finalizarRenovacao();
        }
      } catch (e) {
        // mantém polling
      } finally {
        this.pixStatusLoading = false;
      }
    },

    async copiarPix() {
      try {
        await navigator.clipboard.writeText(this.codigoPix || "");
        this.$toast?.success?.("Código PIX copiado!");
      } catch {
        this.$toast?.danger?.("Não foi possível copiar. Copie manualmente.");
      }
    },

    // ===== Boleto / outros =====
    async finalizarRenovacao() {
      if (this.isLoading || this.renovacaoConcluida || this.telaEncerrada) return false;
      if (this.opcaoSelecionada === "pix" && !this.pixConfirmado) return false;
      if (!this.opcaoSelecionada) {
        return this.$toast?.danger?.("Selecione uma forma de pagamento.");
      }

      this.isLoading = true;
      this.setLoading(true);

      try {
        const res = await this.$http.post("/contrato-fatura/assinatura", {
          id_empresa: this.assinatura.id_empresa,
          id_assinatura: this.assinatura.id_assinatura,
          id_plan: this.assinatura.id_plan,
          modalidade: this.modalidadeSelecionada,
          forma_pagamento: this.opcaoSelecionada,
          valor: this.valorSelecionado,
          cnpj_cpf: this.assinatura.cnpj_cpf,
          email_fatura: this.licenca.email,
          apps_renovacao: this.appsRenovacaoPayload(),

          // plugins selecionados (pra registrar do mesmo jeito do pix)
          plugins: Object.keys(this.plugins)
            .filter((k) => this.plugins[k]?.ativo)
            .map((k) => ({
              key: k,
              nome: this.plugins[k].nome,
              valor: this.plugins[k].valor,
            })),
        });

        if (res?.data?.status === false) {
          throw new Error(res.data.msg || "Não foi possível concluir a renovação.");
        }
        this.renovacaoConcluida = true;
        this.bloquearSaidaPix = false;
        this.stopPixPolling();
        this.$toast?.success?.("Renovação iniciada com sucesso!");
        await this.carregarAssinaturas();
        return true;
      } catch (e) {
        this.$toast?.danger?.(e?.response?.data?.msg || e?.message || "Erro ao renovar. Chame um consultor da Nuts.");
        return false;
      } finally {
        this.isLoading = false;
        this.setLoading(false);
      }
    },

    abrirWhatsApp() {
      const numero = "554935500600";
      const mensagem = encodeURIComponent(
        "Olá, estou sem plano ativo no sistema e gostaria de verificar minha assinatura."
      );
      window.open(`https://wa.me/${numero}?text=${mensagem}`, "_blank");
    },

    isOverdue(item) {
      if (item.pagamento) return false;

      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const due = new Date(item.data_vencimento);
      due.setHours(0, 0, 0, 0);

      return due < today;
    },

    getFaturaStatusMeta(item) {
      const rawStatus = String(item.status_raw || "").toLowerCase();

      if (item.cancelado || ["cancelado", "canceled", "cancelled", "void"].includes(rawStatus)) {
        return { label: "Cancelado", variant: "secondary" };
      }

      if (item.pagamento || ["pago", "paid", "recebido", "received"].includes(rawStatus)) {
        return { label: "Pago", variant: "success" };
      }

      if (this.isOverdue(item) || ["vencido", "overdue", "expired"].includes(rawStatus)) {
        return { label: "Vencido", variant: "danger" };
      }

      return { label: "Pendente", variant: "warning" };
    },
  },
};
